import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchKPIPeople } from "redux/KPIs/KPIPeopleSlice";
import { currencyFormatter, parseCurrency } from "../helpers";
import { createKPIBonusPeople, updateKPIBonusPeople, deleteKPIBonusPeople } from "components/Apis/KPIs";
import toast from "react-hot-toast";
import Select from "react-select";
import { fetchPeople } from "redux/KPIs/peopleSlice";
import { NoTeamLabel } from "views/KPIs";
import { fetchTerminals } from "redux/warehouse/terminalSlice";

const BonusSucessCriteriaTab = () => {
    const quarter = useSelector((state) => state.KPIFilterReducer.quarter);
    const people = useSelector((state) => state.KPIPeopleReducer.people);
    const KPIPeople = useSelector((state) => state.KPINeoBonusCalculatorPeopleReducer.KPIPeople);
    const terminals = useSelector((state) => state.TerminalSlice.terminals);

    const [refresh, setRefresh] = useState(false);

    const serviceTeams = people && terminals
        ? people
            .map((team) => team.name) // Extract the `name` property from each team
            .filter((name) => name !== NoTeamLabel) // Filter out names that match `NoTeamLabel`
        // .concat(["N/A", "Load Planner"])
        // .concat(terminals.map((terminal) => terminal.name))
        : [];

    // Columns to keep from the KPIBonusPerson Objects
    const COLUMNS_TO_KEEP = [
        "service_teams",
        "role",
        "name",
        "quarterly_bonus_opportunity",
        "company_quarterly_load_count_met",
        "continuous_improvement",
        "service_team_gross_profit_goal_met",
        "expedite_gross_profit_goal_met",
        "service_failures",
        "driver_minimums",
    ];

    const MONEY_COLUMNS = ["quarterly_bonus_opportunity"];
    const PERCENTAGE_COLUMNS = [
        "company_quarterly_load_count_met",
        "continuous_improvement",
        "service_team_gross_profit_goal_met",
        "expedite_gross_profit_goal_met",
        "service_failures",
        "driver_minimums",
    ];

    // Remove duplicates from ROLES_OPTIONS
    const ROLES_OPTIONS = terminals ? [
        "Operations Manager",
        "Key Account Manager",
        "Account Manager",
        "Logistics Specialist",
        "Sr Logistics Specialist",
        "Operations Specialist",
        "Sr. Operations Specialist",
        "Team Leader",
        "Sr. Operatons Manager",
        "Load Planner",
        "Sr. Load Planner",
    ].concat(terminals.map((terminal) => `${terminal.name} Terminal Manager`)) : [];

    const SELECT_COLUMNS = [
        { service_teams: serviceTeams },
        { role: ROLES_OPTIONS },
    ];

    const dispatch = useDispatch();

    useEffect(() => {
        if (quarter) {
            dispatch(fetchKPIPeople(quarter));
            dispatch(fetchPeople(quarter));
        }
    }, [quarter, dispatch, refresh]);

    useEffect(() => {
        dispatch(fetchTerminals());
    }, [dispatch]);

    // Function to format a string by replacing underscores and capitalizing words
    const formatHeader = (header) => {
        return header
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (char) => char.toUpperCase());
    };

    const formattedHeaders = COLUMNS_TO_KEEP.concat("Act.").map((header) => formatHeader(header));

    const updateBonusPersonData = async (record) => {
        try {
            const response = await updateKPIBonusPeople(record);
            if (response) {
                dispatch(fetchKPIPeople(quarter));
                toast.success("Data updated successfully!");
            } else {
                throw new Error(response.data.message);
            }
        } catch (error) { // error log
            if (error.response && error.response.data) {
                Object.entries(error.response.data).forEach(([key, value]) => {
                    Object.values(value).forEach((v) => {
                        toast.error(`${formatHeader(key)}: ${v}`);
                    });
                });
            } else {
                toast.error("An unexpected error occurred.");
            }
        }
    };

    return (
        <>
            <div className="max-wdith-container">
                <table className="kpis-goals-table thin bonus-success-criteria-table">
                    <thead>
                        <tr>
                            {formattedHeaders.map((header, index) => (
                                <th key={index}>{header}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {KPIPeople?.map((person, index) => (
                            <tr key={person.id}>
                                {Object.entries(person)
                                    .filter(([key]) => COLUMNS_TO_KEEP.includes(key))
                                    .map(([key, value], idx) => (
                                        <td className={key} key={idx}>
                                            {SELECT_COLUMNS.some((col) => Object.keys(col).includes(key)) ? (
                                                <div className={`select-container ${key}`}>
                                                    <Select
                                                        options={SELECT_COLUMNS.find((col) => Object.keys(col).includes(key))[key].map((option) => ({
                                                            value: option, // `value` is what will be stored
                                                            label: option, // `label` is what will be displayed
                                                        }))}
                                                        isMulti={key === "service_teams"} // Enable multi-select only for "service_teams"
                                                        placeholder="Empty select"
                                                        onChange={(selectedOptions) => {
                                                            updateBonusPersonData({
                                                                id: person.id,
                                                                [key]: key === "service_teams"
                                                                    ? selectedOptions.map((option) => option.value) // For multi-select, extract all values
                                                                    : selectedOptions.value, // For single-select, extract the single value
                                                            });
                                                        }}
                                                        defaultValue={
                                                            key === "service_teams"
                                                                ? value?.map((val) => ({ value: val.name, label: val.name })) // For multi-select, map to { value, label }
                                                                : value // For single-select, use a single object
                                                                    ? { value: value, label: value }
                                                                    : null
                                                        }
                                                        className="select-dropdown"
                                                        classNamePrefix="react-select"
                                                        menuPortalTarget={document.body} // Render dropdown in document.body
                                                        styles={{
                                                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                                        }}
                                                    />
                                                </div>
                                            ) : (
                                                <input
                                                    className="kpi-input"
                                                    defaultValue={
                                                        MONEY_COLUMNS.includes(key)
                                                            ? currencyFormatter.format(value)
                                                            : PERCENTAGE_COLUMNS.includes(key)
                                                                ? `${value}%`
                                                                : value
                                                    }
                                                    placeholder="Empty input"
                                                    onBlur={(e) => {
                                                        const newValue = e.target.value;
                                                        if (newValue === value) {
                                                            return;
                                                        }
                                                        updateBonusPersonData({
                                                            id: person.id,
                                                            [key]: MONEY_COLUMNS.includes(key) || PERCENTAGE_COLUMNS.includes(key)
                                                                ? parseCurrency(newValue)
                                                                : newValue,
                                                        });
                                                    }}
                                                />
                                            )}
                                        </td>
                                    ))}
                                <td className="actions-cell">
                                    <button
                                        className="btn-xs action-btn btn-delete"
                                        onClick={() => {
                                            deleteKPIBonusPeople(person).then(() => {
                                                setRefresh(!refresh);
                                            });
                                        }}
                                        title="Delete"
                                    >
                                        <i className="fa fa-trash"></i>
                                    </button>
                                </td>
                            </tr>
                        ))}
                        <tr>
                            <td colSpan={formattedHeaders.length} style={{ textAlign: 'center' }}>
                                <button
                                    className="new-row-btn"
                                    onClick={() => {
                                        createKPIBonusPeople(quarter).then(() => {
                                            setRefresh(!refresh);
                                        });
                                    }}
                                >
                                    <div className="sign">+</div>
                                    <div className="text">New Member</div>
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </>
    );
};

export default BonusSucessCriteriaTab;